/**
 * SectionChannels — por dónde puede entrar una reserva.
 *
 * La sección responde a una sola pregunta del cliente: "¿dónde se vende mi
 * alojamiento y cómo llega esa reserva hasta aquí?". Por eso separa en dos
 * grupos lo que en el dato es un simple campo `kind`:
 *
 *   portales externos (`ota`)  → escaparates de terceros
 *   canales directos (`direct`) → web propia, equipo y conversación
 *
 * Decisión no obvia: los canales NO se editan en la vista previa. El store no
 * expone ninguna mutación para `channels` a propósito — conectar un portal no
 * es rellenar un formulario, es contratar un channel manager y emparejar
 * anuncios. Mostrarlo como algo editable prometería lo que no existe, así que
 * las tarjetas son informativas y toda la explicación vive en un panel lateral.
 *
 * Tampoco hay credenciales, claves ni direcciones técnicas: eso pertenece a
 * Integraciones y, cuando llegue, a variables de entorno, nunca a esta pantalla.
 */
import React, { useState } from "react";
import {
  House, Buildings, Airplane, Browsers, Note as NoteIcon, WhatsappLogo,
  ShareNetwork, ArrowsLeftRight, Storefront,
} from "@phosphor-icons/react";
import Chip from "@/components/ui-dv/Chip";
import SidePanel from "@/components/ui-dv/SidePanel";
import {
  Block, Card, Note, Advanced, ReadRow, StatusChip,
} from "@/pages/admin/pms/settings/SettingsPrimitives";
import { useSettingsV2 } from "@/lib/settingsV2Store";
import { CHANNEL_STATUS } from "@/data/settingsV2Demo";

/** Icono por canal; si algún día aparece uno nuevo, cae en el genérico. */
const CHANNEL_ICONS = {
  airbnb: House,
  booking: Buildings,
  agoda: Airplane,
  direct_web: Browsers,
  manual: NoteIcon,
  whatsapp: WhatsappLogo,
};

export default function SectionChannels() {
  const { channels } = useSettingsV2();
  const [cmOpen, setCmOpen] = useState(false);

  const otaChannels = channels.filter((c) => c.kind === "ota");
  const directChannels = channels.filter((c) => c.kind !== "ota");
  const viaManager = channels.filter((c) => c.via_channel_manager);

  return (
    <div data-testid="section-channels">
      <Block
        title="Canales de venta"
        description="Cada sitio por el que puede entrar una reserva, con el estado en el que está hoy."
        testId="channels-overview"
        action={
          <button
            type="button"
            onClick={() => setCmOpen(true)}
            data-testid="channels-manage-cm"
            className="dv-btn dv-btn-outline text-xs min-h-[44px] inline-flex items-center gap-1.5"
          >
            <ShareNetwork size={14} weight="bold" />
            Gestionar Channel Manager
          </button>
        }
      >
        <Note testId="channels-intro">
          En la vista previa los canales solo se consultan. Abrir un portal no se resuelve con un
          formulario: hace falta el enlace con un channel manager y emparejar cada tipo de
          alojamiento con su anuncio. La comisión de cada canal se configura en Finanzas e impuestos.
        </Note>

        <ChannelGroup
          groupKey="ota"
          icon={Storefront}
          title="Portales externos"
          description="Escaparates de terceros. La reserva llega ya cerrada por el portal y se recibe aquí."
          channels={otaChannels}
        />

        <ChannelGroup
          groupKey="direct"
          icon={Browsers}
          title="Canales directos"
          description="Reservas sin intermediario: la web propia, el alta manual del equipo y la conversación con el huésped."
          channels={directChannels}
        />

        <Advanced label="Cómo llega una reserva desde cada canal" testId="channels-flow">
          <div className="space-y-3 text-sm text-stone-600 dark:text-stone-300 max-w-2xl">
            <p data-testid="channels-flow-ota">
              <span className="text-stone-900 dark:text-stone-100">Desde un portal externo.</span>{" "}
              El portal vende un tipo de alojamiento y cobra al huésped con sus propias
              condiciones. Esa reserva entra aquí tal y como se vendió y no se modifica desde esta
              plataforma: si hay que cambiar el precio o las fechas, se cambia en el portal y
              vuelve actualizada.
            </p>
            <p data-testid="channels-flow-direct">
              <span className="text-stone-900 dark:text-stone-100">Desde un canal directo.</span>{" "}
              La reserva nace aquí, con las condiciones que hayas configurado, y las noches
              ocupadas se comunican hacia fuera para que ningún portal pueda vender esas mismas
              fechas.
            </p>
            <p data-testid="channels-flow-assignment">
              <span className="text-stone-900 dark:text-stone-100">En los dos casos.</span>{" "}
              Qué alojamiento concreto ocupa cada reserva se decide en esta plataforma, nunca en el
              portal. La limpieza, las incidencias, el portal del huésped y los extractos tampoco
              salen de aquí.
            </p>
          </div>
        </Advanced>
      </Block>

      <SidePanel
        open={cmOpen}
        onClose={() => setCmOpen(false)}
        title="Channel Manager"
        subtitle="Distribución en portales"
        testId="channels-cm-panel"
        footer={
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => setCmOpen(false)}
              data-testid="channels-cm-close"
              className="dv-btn dv-btn-primary text-sm min-h-[44px]"
            >
              Entendido
            </button>
          </div>
        }
      >
        <section data-testid="channels-cm-what">
          <PanelHeading>Qué es</PanelHeading>
          <p className="text-sm text-stone-600 dark:text-stone-300">
            Un channel manager es el enlace entre tu calendario y los portales donde se anuncia el
            alojamiento. Se conecta una sola vez: a partir de ahí reparte la disponibilidad y los
            precios a todos los portales y trae de vuelta las reservas que entran por ellos.
          </p>
        </section>

        <section data-testid="channels-cm-why">
          <PanelHeading>Por qué hace falta</PanelHeading>
          <p className="text-sm text-stone-600 dark:text-stone-300">
            Sin ese enlace, cada portal se actualiza a mano. El riesgo no es el trabajo repetido,
            es que dos huéspedes reserven la misma noche en dos portales distintos antes de que
            dé tiempo a cerrar el calendario.
          </p>
        </section>

        <section data-testid="channels-cm-scope">
          <PanelHeading>Qué sigue estando aquí</PanelHeading>
          <p className="text-sm text-stone-600 dark:text-stone-300">
            El portal vende un tipo de alojamiento. Decidir qué villa concreta ocupa cada reserva,
            la limpieza, las incidencias, el portal del huésped y los extractos de propietario se
            gestionan en esta plataforma.
          </p>
        </section>

        <section data-testid="channels-cm-affected">
          <PanelHeading>Canales que pasarían por él</PanelHeading>
          <div className="flex flex-wrap gap-2" data-testid="channels-cm-list">
            {viaManager.map((c) => (
              <Chip key={c.channel_key} tone="stone" size="sm" data-testid={`channels-cm-chip-${c.channel_key}`}>
                {c.label}
              </Chip>
            ))}
          </div>
          <p className="text-xs text-stone-500 dark:text-stone-400 mt-2">
            Los canales directos no lo necesitan: no dependen de ningún portal.
          </p>
        </section>

        <section data-testid="channels-cm-status">
          <PanelHeading>Estado</PanelHeading>
          <Card>
            <ReadRow
              label="Proveedor conectado"
              value="Ninguno"
              hint="No hay ningún servicio contratado ni configurado"
              testId="channels-cm-provider"
            />
            <ReadRow
              label="Portales a la espera"
              value={viaManager.length}
              hint="Se conectarán todos con el mismo enlace"
              testId="channels-cm-pending"
            />
          </Card>
        </section>

        <Note testId="channels-cm-open-choice">
          En el mercado existen varios proveedores de channel manager, con coberturas y precios
          distintos. La elección sigue abierta y esta pantalla no da ninguno por elegido.
        </Note>
      </SidePanel>
    </div>
  );
}

/** Encabezado corto dentro del panel lateral. */
function PanelHeading({ children }) {
  return (
    <div className="text-[11px] tracking-[0.18em] uppercase text-stone-500 dark:text-stone-400 mb-2">
      {children}
    </div>
  );
}

/** Un grupo de canales con su encabezado explicativo. */
function ChannelGroup({ groupKey, icon: Icon, title, description, channels }) {
  if (channels.length === 0) return null;
  return (
    <div className="mb-6 last:mb-0" data-testid={`channels-group-${groupKey}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className="text-stone-400 shrink-0" />
        <h4 className="text-[11px] tracking-[0.18em] uppercase text-stone-500 dark:text-stone-400">
          {title}
        </h4>
        <span className="text-[11px] text-stone-400 dark:text-stone-500 tabular-nums">
          {channels.length}
        </span>
      </div>
      <p className="text-xs text-stone-500 dark:text-stone-400 mb-3 max-w-2xl">{description}</p>
      <div
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
        data-testid={`channels-list-${groupKey}`}
      >
        {channels.map((c) => (
          <ChannelCard key={c.channel_key} channel={c} />
        ))}
      </div>
    </div>
  );
}

/** Tarjeta informativa de un canal: nombre, para qué sirve y en qué estado está. */
function ChannelCard({ channel }) {
  const Icon = CHANNEL_ICONS[channel.channel_key] || ShareNetwork;
  const status = CHANNEL_STATUS[channel.status] || { label: channel.status, tone: "stone" };

  return (
    <Card className="flex flex-col" testId={`channels-card-${channel.channel_key}`}>
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center shrink-0">
          <Icon size={18} className="text-stone-600 dark:text-stone-300" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-stone-900 dark:text-stone-100 truncate">{channel.label}</div>
          <div className="text-[11px] text-stone-400 dark:text-stone-500">
            {channel.kind === "ota" ? "Portal externo" : "Canal directo"}
          </div>
        </div>
      </div>

      <p className="text-xs text-stone-500 dark:text-stone-400 mt-3 flex-1">{channel.note}</p>

      {channel.via_channel_manager && (
        <div
          className="mt-3 flex items-start gap-2 text-[11px] text-stone-500 dark:text-stone-400"
          data-testid={`channels-via-cm-${channel.channel_key}`}
        >
          <ArrowsLeftRight size={12} className="mt-0.5 shrink-0 text-stone-400" />
          <span>
            Una sola conexión con el channel manager sirve a este portal. No se configura portal a
            portal.
          </span>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-stone-100 dark:border-stone-800">
        <StatusChip tone={status.tone} testId={`channels-status-${channel.channel_key}`}>
          {status.label}
        </StatusChip>
      </div>
    </Card>
  );
}
